import { i18n } from '@libs/i18n';
import { ComputedRef, Ref } from 'vue';

import { type Classification } from './composables/useLiveClassificationSymbolsItemsQuery';
import { type IndustryGroupsOptions } from './composables/useLiveIndustryGroupsQuery';

const { t } = i18n.global;

const SYMBOLS_TYPE_CODES_CATEGORY = {
  300: 'share',
  303: 'share',
  310: 'share',
  309: 'share',
  404: 'shareRight',
  400: 'shareRight',
  403: 'shareRight',
  401: 'shareRight',
  // Exchange traded fund
  315: 'ETF',
  305: 'ETF',
  380: 'ETF',
  200: 'debtBond',
  308: 'debtBond',
  706: 'debtBond',
  208: 'debtBond',
  306: 'debtBond',
  206: 'debtBond',
  301: 'debtBond',
  701: 'commodityDepositCertificate',
  327: 'commodityDepositCertificate',
  311: 'option',
  312: 'option',
  320: 'option',
  321: 'option',
  322: 'option',
  323: 'option',
};

const SYMBOLS_SUB_SECTOR_CODES_CATEGORY = {
  5910: 'mortgageBond',
  6940: 'treasuryBond',
};

const SYMBOL_CHANNEL_PREFIX = 'tse-';
const SYMBOL_BID_ASK_CHANNEL_PREFIX = 'mdp-best-limits-';
const SYMBOL_MARKET_DEPTH_CHANNEL_PREFIX = 'depth-';
const SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX = 'mdp-closing-price-';
const SYMBOL_TRADE_CHANNEL_PREFIX = 'mdp-trade-v2-';
const SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX = 'mdp-trading-status-';
const SYMBOL_INDIVIDUAL_PREFIX = 'mdp-individual-';
const SYMBOL_TRADE_CHANNEL_PRE_OPENING_PREFIX =
  'mdp-theoretical-opening-price-';
const SYMBOL_NAV_PREFIX = 'mdp-nav-';

const TSE_LINK = 'https://www.tsetmc.com/instInfo/';
const CODAL_LINK = 'https://codal.ir/ReportList.aspx?search&Symbol=';

const SYMBOL_STATUS = {
  OEF_SUSPENDED: 'IS',
  OEA_FROZEN: 'AG',
  OEA_SUSPENDED: 'AS',
  OEF_FROZEN: 'IG',
  OEF_RESERVED: 'IR',
  OEA_INSTRUMENT_OPEN: 'A',
  OEA_RESERVED: 'AR',
  OEF_INSTRUMENT_OPEN: 'I',
  NOT_PROVIDED: 'NP',
};

const SYMBOL_STATUS_COLORS = {
  [SYMBOL_STATUS.OEF_SUSPENDED]: 'text-critical-primary',
  [SYMBOL_STATUS.OEA_FROZEN]: 'text-warning-primary',
  [SYMBOL_STATUS.OEA_SUSPENDED]: 'text-warning-primary',
  [SYMBOL_STATUS.OEF_FROZEN]: 'text-critical-primary',
  [SYMBOL_STATUS.OEF_RESERVED]: 'text-critical-primary',
  [SYMBOL_STATUS.OEA_INSTRUMENT_OPEN]: 'text-brand-primary',
  [SYMBOL_STATUS.OEA_RESERVED]: 'text-warning-primary',
  [SYMBOL_STATUS.OEF_INSTRUMENT_OPEN]: 'text-critical-primary',
  [SYMBOL_STATUS.NOT_PROVIDED]: 'text-critical-primary',
  default: 'text-brand-primary',
};

const BROKER_CHANGES_REQUEST_STATUS_ID = {
  canceled: [6],
  done: [2, 4],
  failed: [3, 5],
};

const VALID_INSTRUMENT_TYPES = ['305', '315', '380'];

const VALID_INSTRUMENT_TYPES_STOCK_INFORMATION = [
  '300',
  '303',
  '309',
  '310',
  '313',
  '400',
  '401',
  '403',
  '404',
];

const VALID_SECTOR_INVESTMENT_FUNDS = ['68'];

const FUNDS_SECTOR_CODES = ['68', '69', '59'];

const OPTION_FUNCD_DEFAULT_TYPE = '305';

const INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS = [
  '300',
  '303',
  '309',
];

const OPTION_INSTRUMENT_TYPE_CODES = ['311', '312', '320', '321', '322', '323'];

const FUND_SYMBOLS_ISINS = [
  'IRT3FMTF0001', // FELEZ FARABI
  'IRT1JHSH0001', // JAHESH
  'IRT1FASA0001', // SALAM
  'IRT3LABF0001', // LABKHAND
];

const LABKAHND_ISIN = FUND_SYMBOLS_ISINS[3];

const BASIC_SYMBOLS_SORT_OPTIONS = {
  alphabetical: {
    value: 'alphabetical',
    label: t('symbol.sorts.alphabetical'),
    sortFunction: (a, b) => a.instrumentName.localeCompare(b.instrumentName),
  },
  referencePriceVariationPercentage: {
    value: 'referencePriceVariationPercentage',
    label: t('symbol.sorts.referencePriceVariationPercentage'),
    sortFunction: (a, b) =>
      b.referencePriceVariationPercentage - a.referencePriceVariationPercentage,
  },
  fundIsins: {
    value: 'fundIsins',
    sortFunction: (a, b) => {
      if (
        FUND_SYMBOLS_ISINS.indexOf(a.isin) < FUND_SYMBOLS_ISINS.indexOf(b.isin)
      ) {
        return 1;
      }

      if (
        FUND_SYMBOLS_ISINS.indexOf(a.isin) > FUND_SYMBOLS_ISINS.indexOf(b.isin)
      ) {
        return -1;
      }

      return 0;
    },
  },
};

const ALL_SYMBOLS_SORT_OPTIONS = {
  ...BASIC_SYMBOLS_SORT_OPTIONS,
  portfolioValue: {
    value: 'portfolioValue',
    label: t('symbol.portfolioValue'),
    sortFunction: (a, b) => b.value - a.value,
  },
  portfolioReturnValuePercentage: {
    value: 'portfolioReturnValuePercentage',
    label: t('symbol.sorts.portfolioReturnValuePercentage'),
    sortFunction: (a, b) => b.returnValuePercentage - a.returnValuePercentage,
  },
  portfolioReturnValue: {
    value: 'portfolioReturnValue',
    label: t('symbol.sorts.portfolioReturnValue'),
    sortFunction: (a, b) => b.returnValue - a.returnValue,
  },
};

const SYMBOL_QUERY_KEYS = {
  brokerChange: ['get-broker-change'],
  symbolClassification: ['symbol-classification'],
  buyMarketDepth: (isin: ComputedRef<string | null>) => [
    'buy-market-depth',
    isin,
  ],
  sellMarketDepth: (isin: ComputedRef<string | null>) => [
    'sell-market-depth',
    isin,
  ],
  symbolClassificationItems: (options: Classification) => [
    'symbol-classification-items',
    options,
  ],
  industryGroups: (options: ComputedRef<IndustryGroupsOptions>) => [
    'industry-groups',
    options,
  ],
  symbol: (isin: Ref<string>) => ['symbol', isin],
  searchSymbols: (term: Ref<string>) => ['search', term],
  candleStick: (selectedTimespan: Ref<string>, isin: Ref<string>) => [
    'candle',
    selectedTimespan,
    isin,
  ],
  symbolDeduction: ['symbol-deductions'],
};

const SECONDS_IN_DAY = 86400;
const INTERVAL_TIME_MILLISECOND = {
  oneDay: 60000,
  oneWeek: 300000,
};

const TRADING_STATE_CODES = {
  closed: 0,
  preOpening: 1,
  open: 2,
  forbidden: 3,
} as const;

const PETROCHEMICALS_ISINS = [
  'IRO1PGDR0001',
  'IRO1BFJR0001',
  'IRO1NORI0001',
  'IRO1JPPC0001',
  'IRO1PRDZ0001',
  'IRO1PJMZ0001',
  'IRO3TPEZ0001',
  'IRO1BONA0001',
  'IRO3PMRZ0001',
  'IRO1PFAN0001',
  'IRO1MOBN0001',
  'IRO1PARS0001',
  'IRO3TJPZ0001',
  'IRO3APOZ0001',
  'IRO3PZGZ0001',
  'IRO7PKBP0001',
];

const INITIAL_DEDUCTION_INSTRUMENT_TYPE_CODE = '380';
const INITIAL_DEDUCTION_SUB_SECTOR_CODE = '6811';

export {
  SYMBOLS_SUB_SECTOR_CODES_CATEGORY,
  SYMBOLS_TYPE_CODES_CATEGORY,
  TSE_LINK,
  CODAL_LINK,
  SYMBOL_TRADE_CHANNEL_PREFIX,
  SYMBOL_INSTRUMENT_TRADING_STATE_CHANNEL_PREFIX,
  SYMBOL_STATUS_COLORS,
  SYMBOL_CHANNEL_PREFIX,
  SYMBOL_BID_ASK_CHANNEL_PREFIX,
  SYMBOL_CLOSING_PRICE_CHANNEL_PREFIX,
  SYMBOL_MARKET_DEPTH_CHANNEL_PREFIX,
  SYMBOL_TRADE_CHANNEL_PRE_OPENING_PREFIX,
  BROKER_CHANGES_REQUEST_STATUS_ID,
  VALID_INSTRUMENT_TYPES_STOCK_INFORMATION,
  VALID_SECTOR_INVESTMENT_FUNDS,
  SYMBOL_INDIVIDUAL_PREFIX,
  FUNDS_SECTOR_CODES,
  INSTRUMENT_TYPE_CODES_WITHOUT_PRIORITY_RIGHT_SYMBOLS,
  ALL_SYMBOLS_SORT_OPTIONS,
  SYMBOL_QUERY_KEYS,
  VALID_INSTRUMENT_TYPES,
  INTERVAL_TIME_MILLISECOND,
  SECONDS_IN_DAY,
  LABKAHND_ISIN,
  TRADING_STATE_CODES,
  SYMBOL_NAV_PREFIX,
  OPTION_INSTRUMENT_TYPE_CODES,
  PETROCHEMICALS_ISINS,
  INITIAL_DEDUCTION_INSTRUMENT_TYPE_CODE,
  INITIAL_DEDUCTION_SUB_SECTOR_CODE,
  OPTION_FUNCD_DEFAULT_TYPE,
};
